// ============================================================================
// ENTRENAMIENTO DEL BOT — un solo destino, con pestañas (T-ENT-5, R-ENT-01).
//
// Cristhian, 2-sep-2026: «que todo lo que es entrenamiento del bot quede en una
// sola pestaña, que con un solo icono se entre y ahí estén las configuraciones
// en diferentes pestañas de forma muy iOS profesional».
//
// Las pantallas (Identidad · Conocimiento · Guion · Precios · Catálogo · Calificación · Llamadas) NO
// cambian: aquí solo se les pone la barra de pestañas encima y una URL por
// pestaña (`/entrenamiento/guion`). ⛔ Las URL viejas (`/guion`, `/precios`…)
// SIGUEN sirviendo: redirigen a su pestaña (I-8). Un enlace guardado o el
// simulador no pueden caer en «esa pantalla no existe».
// ============================================================================
import type { ComponentType } from "react";
import { Navigate, NavLink, Route, useParams } from "react-router-dom";
import { Ajustes, Catalogo, Conocimiento, Criterios, GuionConSecciones, InstagramComentarios, Llamadas, Precios, type ContextoPanel } from "@victorix/chat-ai-ui";

export interface PestanaEntrenamiento {
  id: string;
  titulo: string;
  /** La URL con la que nació la pantalla: se redirige, no se rompe. Las pestañas
   *  que nacieron DENTRO del entrenamiento (T-CAL-5: «Calificación») no tienen. */
  vieja?: string;
  Pantalla: ComponentType<{ ctx: ContextoPanel; urlCatalogoErp?: string }>;
}

export const PESTANAS: readonly PestanaEntrenamiento[] = [
  { id: "identidad",   titulo: "Identidad",   vieja: "/ajustes",      Pantalla: Ajustes },
  { id: "conocimiento", titulo: "Conocimiento", vieja: "/conocimiento", Pantalla: Conocimiento },
  // T-ENT-GUION-1 (P2): el guion de siempre + debajo el editor de secciones.
  { id: "guion",       titulo: "Guion",       vieja: "/guion",        Pantalla: GuionConSecciones },
  { id: "precios",     titulo: "Precios",     vieja: "/precios",      Pantalla: Precios },
  { id: "catalogo",    titulo: "Catálogo",    vieja: "/catalogo",     Pantalla: Catalogo },
  // T-CAL-5 (R-CRI-01/02): con qué se califica a un cliente. Nace aquí, sin URL vieja.
  { id: "calificacion", titulo: "Calificación", Pantalla: Criterios },
  // T-ENT-GUION-1 (P3): lo que dice la voz del bot por teléfono. Nace aquí, sin URL vieja.
  { id: "llamadas", titulo: "Llamadas", Pantalla: Llamadas },
  // T-IGPANEL-8A (I-2135): «Comenta PALABRA» de Instagram.
  { id: "instagram", titulo: "Instagram", Pantalla: InstagramComentarios },
];
const POR_DEFECTO = PESTANAS[0];

/** La pestaña que corresponde a un `:pestana` de la URL; lo que no existe cae a Identidad. */
export function pestanaDe(id: string | undefined): PestanaEntrenamiento {
  return PESTANAS.find((p) => p.id === id) ?? POR_DEFECTO;
}

export function Entrenamiento({ ctx, urlCatalogoErp }: { ctx: ContextoPanel; urlCatalogoErp?: string }) {
  const { pestana } = useParams();
  const activa = pestanaDe(pestana);
  const { Pantalla } = activa;
  return (
    <div className="h-full flex flex-col min-w-0">
      <div className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave bg-superficie">
        <h1 className="text-[19px] font-semibold tracking-tight">Entrenamiento del bot</h1>
        <p className="text-[13px] text-tinta-2 mt-0.5 mb-3">
          Quién es, qué sabe, cómo habla, cómo cotiza y qué vende. Todo lo que gobierna a tu bot, en un solo sitio.
        </p>
        {/* Segmento estilo iOS: una píldora por pestaña, la activa «sube». */}
        <nav role="tablist" aria-label="Secciones del entrenamiento"
          className="inline-flex max-w-full overflow-x-auto scroll-fino gap-0.5 p-1 rounded-xl bg-elevado border border-borde-suave">
          {PESTANAS.map((p) => (
            <NavLink key={p.id} to={`/entrenamiento/${p.id}`} role="tab" aria-selected={p.id === activa.id}
              aria-controls={`pestana-${p.id}`}
              className={`shrink-0 h-8 px-3.5 rounded-lg text-[13px] font-medium whitespace-nowrap cursor-pointer
                transition-colors active:scale-[0.97]
                ${p.id === activa.id
                  ? "bg-superficie text-tinta shadow-[var(--shadow-tarjeta)]"
                  : "text-tinta-2 hover:text-tinta"}`}>
              {p.titulo}
            </NavLink>
          ))}
        </nav>
      </div>
      <div id={`pestana-${activa.id}`} role="tabpanel" data-pestana={activa.id} className="flex-1 min-h-0 min-w-0 overflow-auto">
        <Pantalla ctx={ctx} urlCatalogoErp={urlCatalogoErp} />
      </div>
    </div>
  );
}

/**
 * Las rutas del entrenamiento, para montarlas dentro del `<Routes>` de la app y
 * dentro del `MemoryRouter` de las varas — la MISMA lista en los dos sitios.
 */
export function rutasDelEntrenamiento(ctx: ContextoPanel, urlCatalogoErp?: string) {
  return [
    <Route key="entrenamiento" path="entrenamiento/:pestana?"
      element={<Entrenamiento ctx={ctx} urlCatalogoErp={urlCatalogoErp} />} />,
    ...PESTANAS.filter((p) => p.vieja).map((p) => (
      <Route key={p.vieja} path={p.vieja!.slice(1)} element={<Navigate replace to={`/entrenamiento/${p.id}`} />} />
    )),
  ];
}
